import { FC, useContext, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AccountCircleOutlined as AccountCircleOutlinedIcon } from "@material-ui/icons";
import { includes } from "lodash";
import { QueryClient, useQueryClient } from "react-query";

import {
  Button,
  Countdown,
  CustomDialog,
  EssentialImage,
  FavoriteIconButton,
  Image,
} from "components";
import { getThumbnailUrlFromMediaUrl } from "components/MediaPlayer";
import { ASSET_DETAIL_URL, EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { AssetDetail } from "containers";
import { AppContext } from "contexts";
import {
  formatCurrencyAmount,
  formatMarketplaceMedian,
  formatNumber,
} from "lib/format";
import { getTermNiceName, navigateTo } from "lib/helpers";
import * as routes from "routes";
import { OPEN_AUCTION_STATES } from "services/auctions/constants";
import { InventoryListing } from "services/inventory/types";
import { getUseListingsQueryKey } from "services/listings";
import { LISTING_DEAL_STATES, LISTING_KIND } from "services/listings/constants";
import { ListingState } from "services/listings/types";
import theme from "theme";

import DetailsTable, { DetailsTableRow } from "./DetailsTable";
import ListPriceOrSalePrice from "./ListPriceOrSalePrice";
import SystemTags from "./SystemTags";
import UserNotificationTags from "./UserNotificationTags";

const mediaMaxHeight = "190px";
const baseCardMediaStyles = {
  height: mediaMaxHeight,
  backgroundColor: "black",
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  justifyContent: "center",
};
const useStyles = makeStyles(theme => ({
  actionsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    alignItems: "end",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexGrow: 1,
    height: "100%",
    maxWidth: "440px",
    minWidth: "270px",
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
  },
  idHeading: {
    color: theme.custom.palette.gray.dark,
    fontSize: "0.875rem",
    fontWeight: 700,
    textAlign: "left",
  },
  cardMedia: baseCardMediaStyles,
  cardMediaThumbnail: {
    ...baseCardMediaStyles,
    objectFit: "contain",
  },
  cardMediaYoutube: {
    borderRadius: theme.shape.borderRadius,
    width: "100%",
    maxWidth: "340px",
  },
  cardMediaRoyx: {
    ...baseCardMediaStyles,
    objectFit: "contain",
    padding: "40px",
  },
  listingTitle: {
    fontSize: "1.25rem",
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
  },
  statusContainer: {
    display: "flex",
    justifyContent: "space-between",
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 100,
  },
  systemTags: {
    padding: theme.spacing(1),
  },
  userIcon: {
    borderRadius: theme.shape.borderRadius,
    background: "rgba(255, 255, 255, .8)",
    color: theme.custom.palette.green.main,
    fontSize: "2rem",
    padding: "3px",
  },
}));

const makeDetailRows = (
  isListing: boolean,
  isOpenListing: boolean,
  listing: InventoryListing,
  queryClient: QueryClient,
) => {
  const isAcceptingFinalOffers = listing.state === "accepting_final_offers";

  const listingPriceDetailRowTitle = isOpenListing
    ? "List Price"
    : "Sale Price";
  const auctionPriceDetailRowTitle = includes(
    ["preview", "upcoming", "listed"],
    listing.state,
  )
    ? "Starting Price"
    : listing.state === "live"
    ? "Current Price"
    : "Sale Price";

  const detailRows: DetailsTableRow[] =
    isListing && !isAcceptingFinalOffers
      ? [
          { title: "Term", data: getTermNiceName(listing.term) },
          {
            title: listingPriceDetailRowTitle,
            data: <ListPriceOrSalePrice listing={listing} />,
          },
          {
            title: "Last 12 mo. Earnings",
            data: formatCurrencyAmount("USD", listing.ltm),
          },
          {
            title: "Dollar Age",
            data: formatNumber(Number(listing.dollar_age), "", 2),
          },
          {
            title: "Marketplace Median",
            data: formatMarketplaceMedian(listing.marketplace_median),
          },
        ]
      : isAcceptingFinalOffers
      ? [
          { title: "Term", data: getTermNiceName(listing.term) },
          {
            title: "Current Price",
            data: <ListPriceOrSalePrice listing={listing} />,
          },
          {
            title: "Last 12 mo. Earnings",
            data: formatCurrencyAmount("USD", listing.ltm),
          },
          {
            title: "Dollar Age",
            data: formatNumber(Number(listing.dollar_age), "", 2),
          },
        ]
      : [
          // Auction or Resale auction
          { title: "Term", data: getTermNiceName(listing.term) },
          {
            title: "Last 12 mo. Earnings",
            data: formatCurrencyAmount("USD", listing.ltm),
          },
          {
            title: "Dollar Age",
            data: !!Number(listing.dollar_age)
              ? formatNumber(Number(listing.dollar_age), "", 2)
              : EMPTY_VALUE_PLACEHOLDER,
          },
          {
            title: auctionPriceDetailRowTitle,
            data: <ListPriceOrSalePrice listing={listing} />,
            spacerAfterRow: !(listing.state === "live"),
          },
        ];

  if (listing.state === "live" || isAcceptingFinalOffers) {
    detailRows.push({
      title: "Time Remaining",
      data: (
        <Box ml={1}>
          <Countdown
            endTime={
              isAcceptingFinalOffers
                ? listing.accepting_final_offers_end_at
                : listing.close_date
            }
            onEndTimeReached={() => {
              queryClient.refetchQueries("inventory-listings");
            }}
          />
        </Box>
      ),
    });
  }

  return detailRows;
};

export interface ListingGridItemProps {
  listing: InventoryListing;
  prevUrl: string;
  hideUserNotificationTags?: string[];
  refetchOnModalClose?: boolean;
}

const ListingGridItem: FC<ListingGridItemProps> = ({
  listing,
  prevUrl,
  hideUserNotificationTags = [],
  refetchOnModalClose = true,
}) => {
  const classes = useStyles(theme);
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));
  const isUpSm = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  const queryClient = useQueryClient();

  const { user } = useContext(AppContext);
  const isListingOwner = user?.userId === listing.seller_id;

  const hasMedia = listing.media_url || listing.thumbnail_url;
  const isMp4 = listing.media_url && listing.media_url.includes(".mp4");
  const youtubeThumbnail = getThumbnailUrlFromMediaUrl(listing.media_url);

  const isListing = includes(LISTING_KIND, listing.kind);
  const isOpenListing =
    isListing && !includes(LISTING_DEAL_STATES, listing.state);
  const isOpenListingOrAuction =
    isOpenListing || includes(OPEN_AUCTION_STATES, listing.state);

  const detailRows = useMemo(
    () => makeDetailRows(isListing, isOpenListing, listing, queryClient),
    [isListing, isOpenListing, listing, queryClient],
  );

  const [isAssetDetailModalOpen, setIsAssetDetailModalOpen] = useState(false);

  const handleViewListing = () => {
    if (isListing) {
      queryClient.refetchQueries(getUseListingsQueryKey(listing.listing_id));
      if (isUpSm) {
        setIsAssetDetailModalOpen(true);
        window.history.replaceState(
          null,
          null,
          routes.orderBookAssetDetailPreview(listing.listing_id.toString()),
        );
      } else {
        navigateTo(`${ASSET_DETAIL_URL}${listing.listing_id}/`, null, true);
      }
    } else {
      navigateTo(listing.url, null, true);
    }
  };

  const onModalClose = () => {
    if (refetchOnModalClose) {
      queryClient.refetchQueries("inventory-listings");
    }
    window.history.replaceState(null, null, prevUrl);
    setIsAssetDetailModalOpen(false);
  };

  return (
    <Grid container justify="center" item xs={12} md={4}>
      <Card className={classes.card} elevation={3}>
        <Box onClick={isXsDown ? handleViewListing : undefined}>
          {/* wrap ID, statusContainer, and Media in Box to keep them aligned when cards are different height (e.g. different length title) */}
          <Box>
            <Typography className={classes.idHeading} variant="h3" gutterBottom>
              ID: {listing.listing_id}
            </Typography>

            <Box style={{ position: "relative" }}>
              <Box className={classes.statusContainer}>
                <Grid item xs={4} className={classes.systemTags}>
                  {isOpenListingOrAuction && <SystemTags listing={listing} />}
                </Grid>
                <Box className={classes.actionsContainer}>
                  <Grid item>
                    {isListingOwner ? (
                      <AccountCircleOutlinedIcon className={classes.userIcon} />
                    ) : isListing ? (
                      <FavoriteIconButton
                        listingId={listing.listing_id}
                        listingState={listing.state as ListingState}
                      />
                    ) : null}
                  </Grid>
                  <Grid item>
                    {!hideUserNotificationTags.includes(
                      listing.user_notification,
                    ) && (
                      <UserNotificationTags
                        userNotification={listing.user_notification}
                      />
                    )}
                  </Grid>
                </Box>
              </Box>

              {hasMedia ? (
                isMp4 ? (
                  <CardMedia
                    className={classes.cardMedia}
                    component="video"
                    image={listing.media_url}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : !!listing.thumbnail_url ? (
                  <Box className={classes.cardMedia}>
                    <CardMedia
                      className={classes.cardMediaThumbnail}
                      component="img"
                      image={listing.thumbnail_url}
                    />
                  </Box>
                ) : (
                  <Box className={classes.cardMedia}>
                    <EssentialImage>
                      <CardMedia
                        className={classes.cardMediaYoutube}
                        image={youtubeThumbnail}
                        component="img"
                      />
                    </EssentialImage>
                  </Box>
                )
              ) : (
                <Box className={classes.cardMediaRoyx}>
                  <Image name="re-logo-white.svg" />
                </Box>
              )}
            </Box>
          </Box>

          <Tooltip title={listing.title || ""}>
            <Box my={2}>
              <Typography className={classes.listingTitle} variant="h2">
                {listing.title}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        {/* wrap DetailsTable and Button in Box to keep them aligned when cards are different height (e.g. different length title) */}
        <Box>
          <DetailsTable rows={detailRows} />
          <Button
            variant={isOpenListingOrAuction ? "contained" : "outlined"}
            fullWidth
            onClick={handleViewListing}
            style={{ marginTop: theme.spacing(1) }}
          >
            View Listing
          </Button>
        </Box>
      </Card>
      {isAssetDetailModalOpen && (
        <CustomDialog
          onClose={onModalClose}
          title={
            listing.listing_id
              ? `Listing ID (${listing.listing_id})`
              : undefined
          }
          openInNew={() =>
            navigateTo(`${ASSET_DETAIL_URL}${listing.listing_id}/`, null, true)
          }
        >
          {listing.listing_id && (
            <AssetDetail isModal={{ listingId: listing.listing_id }} />
          )}
        </CustomDialog>
      )}
    </Grid>
  );
};

export default ListingGridItem;
